import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { SparkControls, SparkRenderer, SplatMesh } from "@sparkjsdev/spark";
import { MobileJoystick, isMobileDevice } from "./mobileJoystick";

export type ViewerMode = "orbit" | "fly";

/** Paint a futuristic marker glyph into a canvas and wrap it in a THREE
 *  texture. The design has four layers: a soft radial glow, a broken outer
 *  ring (the "scanner"), a continuous inner ring, and a bright white core.
 *  Each tag gets its own texture so individual marker colours come through
 *  without overlap. Tints are derived from the input colour. */
function createMarkerTexture(colorHex: number): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return new THREE.CanvasTexture(canvas);
  const cx = size / 2;
  const cy = size / 2;
  const r = (colorHex >> 16) & 0xff;
  const g = (colorHex >> 8) & 0xff;
  const b = colorHex & 0xff;
  const rgba = (a: number) => `rgba(${r},${g},${b},${a})`;

  // Outer soft glow halo.
  const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.5);
  halo.addColorStop(0, rgba(0.55));
  halo.addColorStop(0.35, rgba(0.25));
  halo.addColorStop(0.7, rgba(0.08));
  halo.addColorStop(1, rgba(0));
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, size, size);

  // Broken outer "scanner" ring — four arcs separated by gaps. Gives the
  // marker the targeting-reticle look common in sci-fi UIs.
  ctx.strokeStyle = rgba(0.95);
  ctx.lineWidth = 2;
  for (let i = 0; i < 4; i += 1) {
    const start = (i * Math.PI) / 2 + Math.PI / 9;
    const end = ((i + 1) * Math.PI) / 2 - Math.PI / 9;
    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.38, start, end);
    ctx.stroke();
  }

  // Continuous inner ring.
  ctx.strokeStyle = rgba(1);
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.22, 0, Math.PI * 2);
  ctx.stroke();

  // Bright white-hot core blending into the tag colour.
  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.12);
  core.addColorStop(0, "rgba(255,255,255,1)");
  core.addColorStop(0.45, rgba(1));
  core.addColorStop(1, rgba(0));
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.12, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Shape options for `addPointMarker`. Used by the two tag layers to make
 *  asset tags and sensor tags visually distinct on the splat:
 *    - "sprite": canvas-painted reticle, faces camera. Default — used by the
 *      measure tool because it reads cleanly as a single point.
 *    - "cube": billboard-free box in the tag color. Used for asset tags.
 *    - "sphere": billboard-free sphere in the tag color. Used for sensor tags.
 */
export type MarkerShape = "sprite" | "cube" | "sphere";

export interface SparkViewerHandle {
  /** Cast a ray from the current mouse NDC position; resolves to a world point or null. */
  pickAtNDC: (ndcX: number, ndcY: number) => THREE.Vector3 | null;
  /** Add a marker at a world point. Returns the object so the caller can
   *  remove it or attach a tag identifier via userData. */
  addPointMarker: (
    worldPos: THREE.Vector3,
    color?: number,
    options?: { shape?: MarkerShape },
  ) => THREE.Object3D;
  /** Remove a previously added marker. */
  removeMarker: (obj: THREE.Object3D) => void;
  /** Draw a thin line between two world points. Returns the line for later removal. */
  addLine: (a: THREE.Vector3, b: THREE.Vector3, color?: number) => THREE.Line;
  removeLine: (line: THREE.Line) => void;
  /** Re-fit the camera to the splat's bounding sphere (whole model in view, from outside). */
  recenter: () => void;
  /** Place the camera INSIDE the scene, near its centre — the default spawn pose. */
  goHome: () => void;
  /** Step the camera halfway closer to the splat center, animated. */
  zoomIn: () => void;
  /** Step the camera 1.5x further from the splat center, animated. */
  zoomOut: () => void;
  /** Animate the camera to focus on a specific world-space point (e.g. a tag). */
  zoomToPoint: (point: THREE.Vector3) => void;
  /** Briefly highlight a marker (e.g. one previously returned from addPointMarker). */
  highlightMarker: (obj: THREE.Object3D, options?: { durationMs?: number; scale?: number }) => void;
  /** Set the perspective camera's field of view (in degrees, 20–110 sensible). */
  setFov: (degrees: number) => void;
  /** Read the current field of view (in degrees). */
  getFov: () => number;
  /** Project a world point into canvas-local pixel coordinates. `visible` is
   *  false when the point is behind the camera or outside the view frustum. */
  projectToScreen: (point: THREE.Vector3) => { x: number; y: number; visible: boolean };
}

interface SparkViewerProps {
  /** Pre-signed S3 GET URL pointing at the .ply / .splat / .spz / .ksplat. */
  viewUrl: string;
  /** Camera control mode. Orbit = drag rotates around the splat center (natural for inspecting a model);
   *  Fly = WASD + mouse-look first-person (natural for walking through a scene). Defaults to 'orbit'. */
  mode?: ViewerMode;
  /** Called when the user clicks the canvas; receives the picked 3D world point (or null if the ray missed). */
  onPick?: (point: THREE.Vector3 | null, ndc: { x: number; y: number }) => void;
  /** Optional className applied to the wrapping div. */
  className?: string;
  /** Called when the splat fails to load (network, decode, etc.). */
  onLoadError?: (message: string) => void;
  /** Status events for the parent's UX. Currently: "raycast-ready" fires
   *  once the splat's wasm is callable for picking. Lets the parent show a
   *  "still initialising" hint instead of a misleading "off the model"
   *  toast when the user clicks early. */
  onStatus?: (event: "raycast-ready" | "raycast-unavailable") => void;
  /** Fired when the user clicks an existing 3D marker (a sprite previously
   *  added via addPointMarker). When this fires, `onPick` does NOT — the
   *  marker takes precedence over the splat pick so clicking a tag selects
   *  it rather than placing a new one underneath. */
  onMarkerPick?: (marker: THREE.Object3D) => void;
  /** Streamed bytes-loaded / total-bytes as the splat downloads. `total` is
   *  0 if the server omits Content-Length, in which case the parent should
   *  show an indeterminate bar. Fires once per chunk arriving from fetch. */
  onLoadProgress?: (loaded: number, total: number) => void;
  /** Fires the first time the splat's bounding box becomes finite — i.e.
   *  when the renderer has actually drawn it. Useful as the signal to hide
   *  a loading overlay since `initialized` resolves *before* the first
   *  visible frame in some spark versions. */
  onSplatReady?: () => void;
}

/**
 * Three.js + Spark JS canvas wrapper.
 *
 * Spark sits on top of THREE.WebGLRenderer — we instantiate the WebGLRenderer,
 * add a SparkRenderer to the scene, then drop in a SplatMesh pointed at the
 * pre-signed S3 URL. Spark's docs don't ship orbit/raycast helpers so we use
 * the stock Three.js facilities for both.
 *
 * Picking against a Gaussian splat is approximate — there is no exact surface
 * to raycast. We project a ray from the camera through the click NDC, and
 * intersect it against the splat's bounding sphere as a reasonable proxy. The
 * resulting world point lives on the splat's bounding shell, which is good
 * enough for placing tags and measuring distances in the typical 1–10m scene.
 */
export const SparkViewer = forwardRef<SparkViewerHandle, SparkViewerProps>(
  function SparkViewer({ viewUrl, mode = "orbit", onPick, onLoadError, onStatus, onMarkerPick, onLoadProgress, onSplatReady, className }, ref) {
    // Keep a live ref to the latest callbacks so the scene's pointer handler
    // (captured once at mount via the [viewUrl]-deps effect) always invokes
    // the CURRENT closures. Without this, switching the parent's `tool` from
    // "navigate" to "tag" would do nothing — the old handler captured at
    // mount would still see tool="navigate" and ignore the click.
    const onPickRef = useRef(onPick);
    const onLoadErrorRef = useRef(onLoadError);
    const onStatusRef = useRef(onStatus);
    const onMarkerPickRef = useRef(onMarkerPick);
    const onLoadProgressRef = useRef(onLoadProgress);
    const onSplatReadyRef = useRef(onSplatReady);
    useEffect(() => { onPickRef.current = onPick; }, [onPick]);
    useEffect(() => { onLoadErrorRef.current = onLoadError; }, [onLoadError]);
    useEffect(() => { onStatusRef.current = onStatus; }, [onStatus]);
    useEffect(() => { onMarkerPickRef.current = onMarkerPick; }, [onMarkerPick]);
    useEffect(() => { onLoadProgressRef.current = onLoadProgress; }, [onLoadProgress]);
    useEffect(() => { onSplatReadyRef.current = onSplatReady; }, [onSplatReady]);
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const splatRef = useRef<SplatMesh | null>(null);
    const markersGroupRef = useRef<THREE.Group | null>(null);
    const recenterRef = useRef<(() => void) | null>(null);
    const goHomeRef = useRef<(() => void) | null>(null);
    const zoomInRef = useRef<(() => void) | null>(null);
    const zoomOutRef = useRef<(() => void) | null>(null);
    const zoomToPointRef = useRef<((point: THREE.Vector3) => void) | null>(null);
    const highlightMarkerRef = useRef<((obj: THREE.Object3D, options?: { durationMs?: number; scale?: number }) => void) | null>(null);
    // True once spark's main-thread WASM (which exposes raycast_splats) has
    // loaded. Rendering uses a separate sort-worker wasm and is ready much
    // earlier, so the splat can be visible while raycast still throws
    // "raycast_splats is undefined". Gate clicks on this so the user gets
    // a clear "loading" feedback instead of a confusing miss.
    const raycastReadyRef = useRef(false);
    // Screen-space position of the "pick in progress" indicator. Set when
    // the user releases a tap and we're about to run the synchronous spark
    // raycast (which can take 100s of ms on large splats). The spinner
    // overlay reads this; null = no pick running.
    const [pickIndicator, setPickIndicator] = useState<{ x: number; y: number } | null>(null);
    // Active camera-controls binding (one of OrbitControls or SparkControls).
    // Lives in a ref so the animation loop reads the current one without the
    // main scene effect having to re-run when the user toggles modes.
    //
    // Spark caveat: SparkControls doesn't expose dispose(); its listeners are
    // permanently attached to the canvas. So we lazily create ONE instance
    // and just toggle its `enable` flag across mode swaps to avoid leaking
    // pointerdown handlers. OrbitControls has a proper dispose and gets
    // created fresh each time we enter orbit mode.
    interface ControlsBinding {
      kind: "orbit" | "fly";
      orbit?: OrbitControls;
      spark?: SparkControls;
      step: () => void;
      dispose: () => void;
    }
    const controlsBindingRef = useRef<ControlsBinding | null>(null);
    const sparkControlsRef = useRef<SparkControls | null>(null);

    useImperativeHandle(ref, () => ({
      pickAtNDC(ndcX, ndcY) {
        return pickFromSplat(ndcX, ndcY);
      },
      addPointMarker(worldPos, color = 0x6366f1, options) {
        const shape = options?.shape ?? "sprite";
        const group = markersGroupRef.current;

        if (shape === "cube") {
          // Cube marker — wraps a colored box plus its white edge lines in
          // a Group so the caller still gets a single Object3D back. Edges
          // give the marker a 3D affordance against the bright splat
          // surface; the box itself is `depthTest:false` so it never gets
          // absorbed by the Gaussians behind it.
          const wrapper = new THREE.Group();
          const boxGeom = new THREE.BoxGeometry(0.16, 0.16, 0.16);
          const boxMat = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false,
          });
          const box = new THREE.Mesh(boxGeom, boxMat);
          box.renderOrder = 999;
          wrapper.add(box);
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(boxGeom),
            new THREE.LineBasicMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 0.85,
              depthTest: false,
            }),
          );
          edges.renderOrder = 1000;
          wrapper.add(edges);
          wrapper.position.copy(worldPos);
          group?.add(wrapper);
          return wrapper;
        }

        if (shape === "sphere") {
          // Sphere marker — solid sphere in the tag color, with a thin
          // white outline. Stays distinct from a cube marker at any angle.
          const wrapper = new THREE.Group();
          const sphGeom = new THREE.SphereGeometry(0.1, 24, 16);
          const sphMat = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false,
          });
          const sphere = new THREE.Mesh(sphGeom, sphMat);
          sphere.renderOrder = 999;
          wrapper.add(sphere);
          const outline = new THREE.Mesh(
            new THREE.SphereGeometry(0.105, 24, 16),
            new THREE.MeshBasicMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 0.35,
              depthTest: false,
              depthWrite: false,
              side: THREE.BackSide,
            }),
          );
          outline.renderOrder = 998;
          wrapper.add(outline);
          wrapper.position.copy(worldPos);
          group?.add(wrapper);
          return wrapper;
        }

        // Default: futuristic sprite marker — canvas-painted glow with a
        // broken outer ring and bright white core. Used by the measure
        // tool because the broken reticle reads cleanly as a "this exact
        // point" indicator.
        const texture = createMarkerTexture(color);
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          opacity: 1,
        });
        const sprite = new THREE.Sprite(material);
        sprite.position.copy(worldPos);
        sprite.renderOrder = 999;
        sprite.scale.setScalar(0.22);
        group?.add(sprite);
        return sprite;
      },
      removeMarker(obj) {
        markersGroupRef.current?.remove(obj);
        // Walk the subtree (cube/sphere markers are Groups; sprite markers
        // are leaf Sprites). Dispose every geometry, material, and texture
        // exactly once so repeated tag refreshes don't leak GPU memory.
        obj.traverse((child) => {
          const m = child as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
          if (m.geometry && typeof m.geometry.dispose === "function") {
            m.geometry.dispose();
          }
          const materials = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
          for (const mat of materials) {
            const withMap = mat as THREE.Material & { map?: THREE.Texture | null };
            if (withMap.map) withMap.map.dispose();
            mat.dispose();
          }
        });
      },
      addLine(a, b, color = 0x10b981) {
        const geom = new THREE.BufferGeometry().setFromPoints([a, b]);
        const mat = new THREE.LineBasicMaterial({ color });
        const line = new THREE.Line(geom, mat);
        markersGroupRef.current?.add(line);
        return line;
      },
      removeLine(line) {
        markersGroupRef.current?.remove(line);
        line.geometry.dispose();
        (line.material as THREE.Material).dispose();
      },
      recenter() {
        recenterRef.current?.();
      },
      goHome() {
        goHomeRef.current?.();
      },
      zoomIn() {
        zoomInRef.current?.();
      },
      zoomOut() {
        zoomOutRef.current?.();
      },
      zoomToPoint(point) {
        zoomToPointRef.current?.(point);
      },
      highlightMarker(mesh, options) {
        highlightMarkerRef.current?.(mesh, options);
      },
      setFov(degrees) {
        const camera = cameraRef.current;
        if (!camera) return;
        camera.fov = Math.max(10, Math.min(140, degrees));
        camera.updateProjectionMatrix();
      },
      getFov() {
        return cameraRef.current?.fov ?? 75;
      },
      projectToScreen(point) {
        const camera = cameraRef.current;
        const renderer = rendererRef.current;
        if (!camera || !renderer) return { x: 0, y: 0, visible: false };
        const ndc = point.clone().project(camera);
        // ndc.z > 1 → behind near plane (and effectively behind camera for
        // points the user can't see). Off-screen ndc.x/y also hides the label.
        const visible =
          ndc.z < 1 && ndc.z > -1 &&
          ndc.x >= -1.05 && ndc.x <= 1.05 &&
          ndc.y >= -1.05 && ndc.y <= 1.05;
        const rect = renderer.domElement.getBoundingClientRect();
        const x = (ndc.x * 0.5 + 0.5) * rect.width;
        const y = (-ndc.y * 0.5 + 0.5) * rect.height;
        return { x, y, visible };
      },
    }));

    // Concentric NDC sample offsets used by pickFromSplat. The first entry
    // is (0, 0) — the exact click. Each subsequent ring tries N directions
    // at the given NDC radius. NDC space is -1..1, so 0.20 NDC ≈ 10% of the
    // viewport's full width (≈ 100 px on a 1000 px viewport — about a
    // finger's width of click tolerance). The largest ring catches sparse
    // splats where the visible model has wide gaps between Gaussians.
    //
    // Cost: 1 + 6 * 10 = 61 wasm raycast_splats calls per click. Each is
    // O(num_splats); on a 1M-splat scene this is a few tens of ms. We stop
    // expanding as soon as ANY ring yields a hit, so dense regions resolve
    // in 1-9 rays.
    const PICK_SAMPLE_RADII = [0, 0.01, 0.025, 0.05, 0.1, 0.15, 0.2];
    const PICK_ANGLE_STEPS = 10;
    const PICK_SAMPLES: Array<[number, number]> = (() => {
      const out: Array<[number, number]> = [];
      for (const r of PICK_SAMPLE_RADII) {
        if (r === 0) {
          out.push([0, 0]);
          continue;
        }
        for (let i = 0; i < PICK_ANGLE_STEPS; i += 1) {
          const angle = (i / PICK_ANGLE_STEPS) * Math.PI * 2;
          out.push([Math.cos(angle) * r, Math.sin(angle) * r]);
        }
      }
      return out;
    })();

    /**
     * Strict spark-raycast picker, matching the pattern in the spark.js
     * raycasting docs:
     *
     *   const intersects = raycaster.intersectObjects(scene.children);
     *   const splatIndex = intersects.findIndex((i) => i.object instanceof SplatMesh);
     *
     * We use `scene.children` (not `[splat]`) because spark's example does,
     * and that path is the one spark tests against. Multi-sampling adds
     * click tolerance for sparse Gaussian regions — every sample ray uses
     * the same pattern. We return the closest splat-hit across all samples
     * (a single point, no centroid averaging — the user reported the
     * centroid version wasn't sticking to the surface).
     *
     * Returns null when no sample ray hits a SplatMesh at all, meaning the
     * click is genuinely off the model.
     */
    /**
     * Hybrid picker that works whether spark's main-thread wasm has loaded
     * or not. Two stages:
     *
     *   1. Ray-cast against the splat's bounding box (pure geometry, never
     *      throws). If the ray misses the box, the click is genuinely off
     *      the model → return null and the parent shows the "off model" toast.
     *
     *   2. Try spark's wasm-backed raycast on the center sample. If it
     *      returns hits, take the closest surface point — that's the ideal
     *      result. If it throws (wasm not ready) or returns no hits, fall
     *      back to the midpoint of the ray's traversal through the bbox.
     *      That puts the tag *inside* the splat volume (not perfect, but
     *      never in the air) so the user can keep working while the wasm
     *      issue is resolved by upgrading spark.
     *
     * Reports its mode via `onStatusRef` so the parent can flag fallback
     * mode (where ideal-surface picks aren't possible).
     */
    function pickFromSplat(ndcX: number, ndcY: number): THREE.Vector3 | null {
      const camera = cameraRef.current;
      const splat = splatRef.current;
      const scene = sceneRef.current;
      if (!camera || !splat || !scene) return null;

      splat.updateMatrixWorld(true);
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

      // Stage 1 — bbox gate.
      let bboxLocal: THREE.Box3 | null = null;
      try {
        bboxLocal = splat.getBoundingBox();
      } catch {
        bboxLocal = null;
      }
      if (!bboxLocal || !isFinite(bboxLocal.min.x) || bboxLocal.getSize(new THREE.Vector3()).length() === 0) {
        // Splat hasn't finished loading. Can't tell yet if the click was on
        // or off the model — return null and let the parent show "loading."
        return null;
      }
      const bboxWorld = bboxLocal.clone().applyMatrix4(splat.matrixWorld);
      bboxWorld.expandByScalar(bboxWorld.getSize(new THREE.Vector3()).length() * 0.02);
      const entry = new THREE.Vector3();
      if (!raycaster.ray.intersectBox(bboxWorld, entry)) {
        // Genuine off-model click.
        return null;
      }
      const exit = computeBoxExit(raycaster.ray, bboxWorld) ?? entry;
      const bboxMidpoint = entry.clone().add(exit).multiplyScalar(0.5);

      // Stage 2 — try spark's wasm raycast for an exact surface hit.
      // Fast path: cast a SINGLE center ray first. The vast majority of
      // clicks land directly on the model, so this hits on the first try
      // and saves 60 follow-up raycasts. Spark's raycast iterates every
      // splat in the WASM call, so each ray is the expensive part — cutting
      // 61 → 1 makes the common case ~60× faster.
      const trySingleSplatRay = (
        ox: number, oy: number,
      ): { hit: THREE.Vector3 | null; threw: boolean } => {
        raycaster.setFromCamera(new THREE.Vector2(ox, oy), camera);
        try {
          const hits = raycaster.intersectObjects(scene.children);
          const splatHit = hits.find((it) => it.object instanceof SplatMesh && it.point);
          return { hit: splatHit?.point?.clone() ?? null, threw: false };
        } catch (err) {
          console.warn(
            "SparkViewer: spark raycast threw — using bbox-midpoint fallback. " +
            "Upgrade @sparkjsdev/spark (installed 0.1.10, latest 2.0) for a real fix.",
            err,
          );
          onStatusRef.current?.("raycast-unavailable");
          return { hit: null, threw: true };
        }
      };

      const first = trySingleSplatRay(ndcX, ndcY);
      if (first.hit) {
        if (!raycastReadyRef.current) {
          raycastReadyRef.current = true;
          onStatusRef.current?.("raycast-ready");
        }
        return first.hit;
      }
      // Wasm threw at all on the first try → no point sampling the other
      // 60 rays, they'll throw too. Skip straight to the bbox fallback.
      if (first.threw) {
        return bboxMidpoint;
      }

      // Slow path: center missed (sparse splat at this exact pixel). Sweep
      // the surrounding NDC samples and take the closest hit. We start at
      // index 1 because index 0 is (0,0) — already tried as `first`.
      let surfaceHit: THREE.Vector3 | null = null;
      let surfaceDistance = Infinity;
      for (let i = 1; i < PICK_SAMPLES.length; i += 1) {
        const [dx, dy] = PICK_SAMPLES[i];
        raycaster.setFromCamera(new THREE.Vector2(ndcX + dx, ndcY + dy), camera);
        let intersects: THREE.Intersection[] = [];
        try {
          intersects = raycaster.intersectObjects(scene.children);
        } catch {
          break; // wasm broke mid-sweep — bail to bbox fallback
        }
        const hit = intersects.find((it) => it.object instanceof SplatMesh && it.point);
        if (hit && hit.point && hit.distance < surfaceDistance) {
          surfaceHit = hit.point.clone();
          surfaceDistance = hit.distance;
        }
      }

      if (surfaceHit) {
        if (!raycastReadyRef.current) {
          raycastReadyRef.current = true;
          onStatusRef.current?.("raycast-ready");
        }
        return surfaceHit;
      }

      // No surface hit — use the bbox midpoint. Tag lands inside the splat
      // volume rather than on its actual surface, but at least the user can
      // associate the asset with this part of the model.
      return bboxMidpoint;
    }

    /** Compute where a ray exits an axis-aligned bbox after entering it. */
    function computeBoxExit(ray: THREE.Ray, box: THREE.Box3): THREE.Vector3 | null {
      const o = ray.origin;
      const d = ray.direction;
      const inv = (v: number) => (v === 0 ? 1e30 : 1 / v);
      const tx1 = (box.min.x - o.x) * inv(d.x);
      const tx2 = (box.max.x - o.x) * inv(d.x);
      const ty1 = (box.min.y - o.y) * inv(d.y);
      const ty2 = (box.max.y - o.y) * inv(d.y);
      const tz1 = (box.min.z - o.z) * inv(d.z);
      const tz2 = (box.max.z - o.z) * inv(d.z);
      const tmax = Math.min(Math.max(tx1, tx2), Math.max(ty1, ty2), Math.max(tz1, tz2));
      if (!isFinite(tmax) || tmax < 0) return null;
      return ray.at(tmax, new THREE.Vector3());
    }

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const width = container.clientWidth || 800;
      const height = container.clientHeight || 600;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0b14);

      const camera = new THREE.PerspectiveCamera(75, width / height, 0.01, 1000);
      // Provisional position. Real framing happens once the splat's bounding
      // sphere is available (see auto-frame block in the animation loop) —
      // real-world scans are rarely centered at origin.
      camera.position.set(0, 0, 1);

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      container.appendChild(renderer.domElement);

      const spark = new SparkRenderer({ renderer });
      scene.add(spark);

      // Stream the splat straight from the fetch response into SplatMesh so
      // decode runs in parallel with download. The old path buffered the
      // entire file into a Blob, made an object URL, then handed it to
      // SplatMesh — which meant nothing rendered until the last byte arrived
      // (painful for 100+ MB digital twins). Spark 2.0 added the `stream` /
      // `streamLength` options precisely for this case: chunks are decoded
      // as they land, and the splat becomes visible long before download
      // completes. SplatMesh's own onProgress fires for each chunk so we
      // still drive the parent's progress UI without a manual reader loop.
      //
      // Tracked via mutable closure variables (splat is assigned async).
      // splatRef.current is updated in-place once the SplatMesh exists so
      // the imperative handle methods see it. Until then they no-op safely.
      let splat: SplatMesh | null = null;
      const fetchController = new AbortController();
      (async () => {
        try {
          const resp = await fetch(viewUrl, { signal: fetchController.signal });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          if (!resp.body) throw new Error("Response missing body — cannot stream");
          const totalHeader = resp.headers.get("Content-Length");
          const total = totalHeader ? parseInt(totalHeader, 10) : 0;
          onLoadProgressRef.current?.(0, total);

          // Spark sniffs the splat format (ply / spz / splat / ksplat) from
          // the file extension. The pre-signed S3 URL keeps the original
          // filename in the path before the `?X-Amz-...` query, so we strip
          // the query and take the basename.
          let fileName: string | undefined;
          try {
            const u = new URL(viewUrl, window.location.href);
            const basename = u.pathname.split("/").pop();
            if (basename) fileName = decodeURIComponent(basename);
          } catch {
            // Couldn't parse — let Spark sniff from magic bytes instead.
          }

          if (fetchController.signal.aborted) return;

          // No `lod` flag: spark's LOD mode rebuilds the splat into a tree
          // and can leave `packedSplats.packedArray` empty, which makes
          // SplatMesh.raycast() bail silently. Spark 2.0 explicitly opts
          // splats into raycasting and lets us lower the opacity threshold.
          splat = new SplatMesh({
            stream: resp.body,
            streamLength: total || undefined,
            fileName,
            raycastable: true,
            minRaycastOpacity: 0.05,
            onProgress: (event: ProgressEvent) => {
              // ProgressEvent.total is 0 when lengthComputable is false; in
              // that case fall back to the Content-Length we captured above.
              const reportedTotal = event.total || total;
              onLoadProgressRef.current?.(event.loaded, reportedTotal);
            },
          });
          scene.add(splat);
          splatRef.current = splat;

          splat.initialized.catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : String(err);
            console.error("SparkViewer: splat failed to load", err);
            onLoadErrorRef.current?.(`Failed to load splat: ${msg}`);
          });
        } catch (err: unknown) {
          if (fetchController.signal.aborted) return;
          const msg = err instanceof Error ? err.message : String(err);
          console.error("SparkViewer: fetch failed", err);
          onLoadErrorRef.current?.(`Failed to download splat: ${msg}`);
        }
      })();

      // Kick off the static wasm init in case it helps. The actual gating
      // happens via raycastReadyRef which flips true after the first
      // successful raycast — until then pickFromSplat uses a bbox-midpoint
      // fallback so tagging keeps working even if spark's wasm misbehaves.
      SplatMesh.staticInitialize().catch((err) => {
        console.error("SparkViewer: spark.staticInitialize() rejected", err);
      });

      const markersGroup = new THREE.Group();
      scene.add(markersGroup);
      markersGroupRef.current = markersGroup;

      sceneRef.current = scene;
      cameraRef.current = camera;
      rendererRef.current = renderer;

      // ── Auto-frame after splat loads ─────────────────────────────────
      // SplatMesh exposes an `initialized` Promise + `getBoundingBox()`. But
      // `initialized` resolves when the binary is parsed — when `lod: true`
      // is set, the WebWorker still has to build the LOD tree, and during
      // that window getBoundingBox() returns an empty box. So we can't rely
      // on a one-shot frame inside `.then()`. Instead we poll each animation
      // frame until the box becomes finite and non-empty, then frame once
      // and stop. `initialized` is only used for error reporting now.
      // Camera framing distances, as multiples of the splat's bounding radius.
      //   FRAME_* → pulls back so the whole model is in view (from outside).
      //   HOME_*  → sits the camera near the centre, i.e. INSIDE the scene.
      const FRAME_DISTANCE_FACTOR = 2.5;
      const FRAME_MIN_DISTANCE = 0.5;
      const HOME_DISTANCE_FACTOR = 0.2;
      const HOME_MIN_DISTANCE = 0.3;

      function frameToBoundingBox(
        distanceFactor = FRAME_DISTANCE_FACTOR,
        minDistance = FRAME_MIN_DISTANCE,
      ): boolean {
        const liveSplat = splat ?? splatRef.current;
        if (!liveSplat) return false;
        try {
          liveSplat.updateMatrixWorld(true);
          const box = liveSplat.getBoundingBox();
          if (!box || !isFinite(box.min.x) || !isFinite(box.max.x)) return false;
          const size = box.getSize(new THREE.Vector3());
          const radius = size.length() * 0.5;
          if (radius <= 0 || !isFinite(radius)) return false;
          const center = box.getCenter(new THREE.Vector3()).applyMatrix4(liveSplat.matrixWorld);
          const distance = Math.max(radius * distanceFactor, minDistance);
          camera.position.copy(center).add(new THREE.Vector3(0, 0, distance));
          camera.lookAt(center);
          camera.near = Math.max(0.001, radius * 0.01);
          camera.far = Math.max(radius * 100, 1000);
          camera.updateProjectionMatrix();
          // Orbit needs to know what to orbit. Without this, orbit-drag would
          // rotate around (0,0,0) which is rarely where the splat sits.
          const binding = controlsBindingRef.current;
          if (binding?.kind === "orbit" && binding.orbit) {
            binding.orbit.target.copy(center);
            binding.orbit.update();
          }
          return true;
        } catch (err) {
          console.warn("SparkViewer: frameToBoundingBox failed", err);
          return false;
        }
      }

      let autoFramed = false;
      let frameAttempts = 0;
      const MAX_FRAME_ATTEMPTS = 1800; // ~30 s at 60 fps; covers slow LOD builds + big splats
      function tryAutoFrame() {
        if (autoFramed) return;
        frameAttempts += 1;
        // Spawn the user INSIDE the scene (near its centre) by default, rather
        // than pulling the camera back to view the splat as a distant object.
        if (frameToBoundingBox(HOME_DISTANCE_FACTOR, HOME_MIN_DISTANCE)) {
          autoFramed = true;
          // First successful frame = splat is renderable. Tell the parent
          // so it can hide its loading overlay.
          onSplatReadyRef.current?.();
          return;
        }
        if (frameAttempts >= MAX_FRAME_ATTEMPTS) {
          console.warn(
            "SparkViewer: gave up auto-framing after",
            MAX_FRAME_ATTEMPTS,
            "attempts (bounding box never became finite). Use the Frame button.",
          );
          autoFramed = true; // stop trying
        }
      }

      // Imperative recenter — bypasses the latch so the user can re-fit any
      // time, even after they've flown off into the void.
      recenterRef.current = () => { frameToBoundingBox(); };
      // Home — drop the camera back inside the scene near its centre (the
      // default spawn), for when the user has orbited/flown out and away.
      goHomeRef.current = () => { frameToBoundingBox(HOME_DISTANCE_FACTOR, HOME_MIN_DISTANCE); };

      // ── Animated zoom ────────────────────────────────────────────────
      // A simple 400 ms eased lerp from current camera pose to a target
      // position + lookAt target. Updated per frame in the animation loop.
      // SparkControls modifies camera.position/quaternion each frame too,
      // but we run the zoom step AFTER SparkControls.update() so the final
      // pose is ours during the animation window.
      interface ZoomAnim {
        startPos: THREE.Vector3;
        endPos: THREE.Vector3;
        lookAt: THREE.Vector3;
        startMs: number;
        durationMs: number;
      }
      let activeZoom: ZoomAnim | null = null;

      function startZoomTo(targetPoint: THREE.Vector3, fraction: number) {
        // Move the camera `fraction` of the way from its current position to
        // targetPoint. fraction = 0.5 zooms in by half; fraction = -0.5 backs
        // off (we extend the ray the other direction below).
        const startPos = camera.position.clone();
        let endPos: THREE.Vector3;
        if (fraction >= 0) {
          endPos = startPos.clone().lerp(targetPoint, fraction);
        } else {
          // Negative fraction → step away from target along the same vector.
          const away = startPos.clone().sub(targetPoint).multiplyScalar(-fraction);
          endPos = startPos.clone().add(away);
        }
        activeZoom = {
          startPos,
          endPos,
          lookAt: targetPoint.clone(),
          startMs: performance.now(),
          durationMs: 400,
        };
      }

      function easeOutCubic(t: number): number {
        return 1 - Math.pow(1 - t, 3);
      }

      function stepZoom() {
        if (!activeZoom) return;
        const t = Math.min(1, (performance.now() - activeZoom.startMs) / activeZoom.durationMs);
        const k = easeOutCubic(t);
        camera.position.lerpVectors(activeZoom.startPos, activeZoom.endPos, k);
        camera.lookAt(activeZoom.lookAt);
        // Sync orbit target — otherwise the next drag would whip the camera
        // back to the pre-zoom target.
        const orbit = controlsBindingRef.current?.orbit;
        if (orbit) orbit.target.copy(activeZoom.lookAt);
        if (t >= 1) activeZoom = null;
      }

      function centerFallback(): THREE.Vector3 {
        // For zoom buttons we don't have a click point. Fall back to the
        // splat center if we have one, else camera-forward * 1 unit. The
        // splat ref is nullable — when the user mashes zoom before the
        // splat finishes loading, we still want a sensible target rather
        // than a NPE that kills the render loop.
        if (splat) {
          try {
            splat.updateMatrixWorld(true);
            const box = splat.getBoundingBox();
            if (isFinite(box.min.x) && box.getSize(new THREE.Vector3()).length() > 0) {
              return box.getCenter(new THREE.Vector3()).applyMatrix4(splat.matrixWorld);
            }
          } catch {
            /* fall through */
          }
        }
        const fwd = new THREE.Vector3();
        camera.getWorldDirection(fwd);
        return camera.position.clone().add(fwd);
      }

      zoomInRef.current = () => startZoomTo(centerFallback(), 0.5);
      zoomOutRef.current = () => startZoomTo(centerFallback(), -0.5);
      // Zoom to a specific world-space point and reframe around it. Used by
      // the sidebar "click tag → focus tag" flow. Picks a sensible camera
      // distance: ~3 m from the point along the current view direction.
      zoomToPointRef.current = (point) => {
        const TARGET_DISTANCE = 3;
        const dir = new THREE.Vector3();
        camera.getWorldDirection(dir);
        const endPos = point.clone().add(dir.clone().multiplyScalar(-TARGET_DISTANCE));
        activeZoom = {
          startPos: camera.position.clone(),
          endPos,
          lookAt: point.clone(),
          startMs: performance.now(),
          durationMs: 600,
        };
      };

      // Briefly pulse a marker: scale up + flash brighter, then return to
      // normal. Used to draw the eye to the just-selected tag. Works with
      // both Sprite (current tag markers) and Mesh (legacy).
      highlightMarkerRef.current = (obj, options) => {
        const durationMs = options?.durationMs ?? 1200;
        const peakScale = options?.scale ?? 2.0;
        const baseScale = obj.userData.baseScale ?? obj.scale.x;
        obj.userData.baseScale = baseScale;
        // Sprite and Mesh both expose `.material` but TS can't narrow Object3D
        // to either — go via `unknown` to read whichever the runtime object
        // actually has. Both materials share the .opacity field we mutate.
        const withMaterial = obj as unknown as { material?: THREE.Material };
        const maybeMaterial = withMaterial.material as
          | THREE.SpriteMaterial
          | THREE.MeshBasicMaterial
          | undefined;
        if (!maybeMaterial) return;
        const material: THREE.SpriteMaterial | THREE.MeshBasicMaterial = maybeMaterial;
        const baseOpacity = obj.userData.baseOpacity ?? material.opacity;
        obj.userData.baseOpacity = baseOpacity;
        const startMs = performance.now();
        function tick() {
          const t = Math.min(1, (performance.now() - startMs) / durationMs);
          // 0 → 1 → 0 pulse curve.
          const pulse = 1 - Math.abs(2 * t - 1);
          const scale = baseScale * (1 + (peakScale - 1) * pulse);
          obj.scale.setScalar(scale);
          material.opacity = baseOpacity + (1 - baseOpacity) * pulse;
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            obj.scale.setScalar(baseScale);
            material.opacity = baseOpacity;
          }
        }
        tick();
      };

      function handleDoubleClick(e: MouseEvent) {
        const rect = renderer.domElement.getBoundingClientRect();
        const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        const point = pickFromSplat(ndcX, ndcY);
        if (point) startZoomTo(point, 0.5);
      }
      renderer.domElement.addEventListener("dblclick", handleDoubleClick);

      // Belt-and-braces context-menu suppression. SparkControls uses right-
      // drag for slide/pan, so the OS context menu must never appear or it
      // steals the pointer events mid-drag. Three layers:
      //   1. addEventListener contextmenu (bubbling phase)
      //   2. addEventListener contextmenu (capture phase) so document-level
      //      handlers in unusual setups don't beat us
      //   3. oncontextmenu property as a final backstop
      function suppressContextMenu(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
      }
      renderer.domElement.addEventListener("contextmenu", suppressContextMenu);
      renderer.domElement.addEventListener("contextmenu", suppressContextMenu, true);
      renderer.domElement.oncontextmenu = () => false;

      // ── Mobile joystick ──────────────────────────────────────────────
      // Touch-only devices have no keyboard. The joystick feeds forward/back
      // + strafe into the camera each frame using its local axes so movement
      // matches where the user is looking.
      const joystick = isMobileDevice() ? new MobileJoystick() : null;
      let lastJoystickMs = performance.now();
      const joystickSpeed = 1.5; // world units per second at full deflection

      function handleResize() {
        if (!container) return;
        const w = container.clientWidth;
        const h = container.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }
      window.addEventListener("resize", handleResize);

      // Tap-style click detection via pointerdown/pointerup with a movement
      // threshold. The native `click` event is unreliable here:
      //   * OrbitControls / SparkControls call setPointerCapture on the
      //     canvas during drag — if the user drags even a few pixels the
      //     browser suppresses the `click` event.
      //   * Damping inertia can register tiny mouse moves as motion.
      // A pointerdown/up pair lets us tolerate small jitter and reliably
      // detect taps even mid-orbit.
      let downAt: { x: number; y: number; t: number } | null = null;
      const TAP_MAX_PIXELS = 6;
      const TAP_MAX_MS = 500;
      function handlePointerDown(e: PointerEvent) {
        if (e.button !== 0) return;
        downAt = { x: e.clientX, y: e.clientY, t: performance.now() };
      }
      function handlePointerUp(e: PointerEvent) {
        const start = downAt;
        downAt = null;
        if (!start || e.button !== 0) return;
        const callback = onPickRef.current;
        const markerCallback = onMarkerPickRef.current;
        if (!callback && !markerCallback) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (Math.hypot(dx, dy) > TAP_MAX_PIXELS) return;
        if (performance.now() - start.t > TAP_MAX_MS) return;
        const rect = renderer.domElement.getBoundingClientRect();
        const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        // Try marker raycast first — clicking an existing tag marker should
        // always select it rather than placing a new tag underneath. Sprites
        // and meshes both implement raycast(), so a single pass against the
        // markers group catches both. Skip the splat pick on a hit.
        const markersGroup = markersGroupRef.current;
        if (markersGroup && markersGroup.children.length > 0 && markerCallback) {
          const markerRay = new THREE.Raycaster();
          markerRay.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
          const markerHits = markerRay.intersectObjects(markersGroup.children, false);
          // Only treat hits with a tagId as marker picks — measure-tool
          // spheres also live in this group but shouldn't trigger selection.
          const tagHit = markerHits.find((h) => h.object.userData.tagId);
          if (tagHit) {
            markerCallback(tagHit.object);
            return;
          }
        }
        if (!callback) return;

        // Spark's wasm raycast is synchronous and iterates every splat —
        // can take 100s of ms on large scenes. Without a yield, the
        // browser locks up between pointerup and the modal/marker
        // appearing, and the user thinks the click was dropped.
        //
        // Strategy: show a small "thinking" spinner at the click point,
        // but only if the raycast actually takes >120 ms. Below that the
        // spinner would just flash and be visually noisy. The double rAF
        // ensures React's commit + paint runs before we start blocking.
        const containerEl = containerRef.current;
        const offsetX = containerEl ? e.clientX - containerEl.getBoundingClientRect().left : e.clientX;
        const offsetY = containerEl ? e.clientY - containerEl.getBoundingClientRect().top : e.clientY;
        const indicatorTimer = window.setTimeout(() => {
          setPickIndicator({ x: offsetX, y: offsetY });
        }, 120);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              const point = pickFromSplat(ndcX, ndcY);
              callback(point, { x: ndcX, y: ndcY });
            } finally {
              window.clearTimeout(indicatorTimer);
              setPickIndicator(null);
            }
          });
        });
      }
      renderer.domElement.addEventListener("pointerdown", handlePointerDown);
      renderer.domElement.addEventListener("pointerup", handlePointerUp);

      const FORWARD = new THREE.Vector3();
      const RIGHT = new THREE.Vector3();
      const UP = new THREE.Vector3(0, 1, 0);

      renderer.setAnimationLoop(() => {
        tryAutoFrame();
        controlsBindingRef.current?.step();

        // Joystick: translate in camera-local frame. y > 0 means the knob is
        // pulled DOWN on screen → camera moves backward (matches the snippet
        // contract: positive y == backward).
        if (joystick) {
          const now = performance.now();
          const dt = Math.min(0.05, (now - lastJoystickMs) / 1000);
          lastJoystickMs = now;
          const input = joystick.getInput();
          if (input.active && (input.x !== 0 || input.y !== 0)) {
            camera.getWorldDirection(FORWARD);
            RIGHT.crossVectors(FORWARD, UP).normalize();
            const step = joystickSpeed * dt;
            camera.position.addScaledVector(RIGHT, input.x * step);
            camera.position.addScaledVector(FORWARD, -input.y * step);
            // Orbit target slides with the camera so the orbit pivot stays
            // a fixed offset from the new camera position.
            const orbit = controlsBindingRef.current?.orbit;
            if (orbit) {
              orbit.target.addScaledVector(RIGHT, input.x * step);
              orbit.target.addScaledVector(FORWARD, -input.y * step);
            }
          }
        }

        // Zoom animation last so it wins during the eased lerp.
        stepZoom();

        renderer.render(scene, camera);
      });

      return () => {
        renderer.setAnimationLoop(null);
        window.removeEventListener("resize", handleResize);
        renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
        renderer.domElement.removeEventListener("pointerup", handlePointerUp);
        renderer.domElement.removeEventListener("dblclick", handleDoubleClick);
        renderer.domElement.removeEventListener("contextmenu", suppressContextMenu);
        renderer.domElement.removeEventListener("contextmenu", suppressContextMenu, true);
        renderer.domElement.oncontextmenu = null;
        joystick?.dispose();
        // Controls binding is owned by the mode effect; it disposes itself
        // when it tears down, but if we're unmounting the whole viewer we
        // dispose here too so the user can't see a flash of stale state.
        controlsBindingRef.current?.dispose();
        controlsBindingRef.current = null;
        // The cached SparkControls instance has listeners attached to the
        // about-to-be-removed canvas; drop the ref so the next mount creates
        // a fresh one for the new canvas.
        sparkControlsRef.current = null;
        markersGroup.traverse((obj) => {
          if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
            obj.geometry.dispose();
            const mat = obj.material as THREE.Material | THREE.Material[];
            if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
            else mat.dispose();
          }
        });
        // Abort an in-flight splat fetch (unmounting before download
        // completes happens often in dev with StrictMode + during nav).
        fetchController.abort();
        // SplatMesh exposes its own teardown via dispose() if available; guard
        // against API drift.
        if (splat) {
          const splatAny = splat as unknown as { dispose?: () => void };
          if (typeof splatAny.dispose === "function") splatAny.dispose();
        }
        renderer.dispose();
        if (renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
        splatRef.current = null;
        sceneRef.current = null;
        cameraRef.current = null;
        rendererRef.current = null;
        markersGroupRef.current = null;
        recenterRef.current = null;
        goHomeRef.current = null;
        zoomInRef.current = null;
        zoomOutRef.current = null;
        zoomToPointRef.current = null;
        highlightMarkerRef.current = null;
        raycastReadyRef.current = false;
      };
      // Only viewUrl rebuilds the scene. Mode swaps are handled by a separate
      // effect (below) that only touches the controls binding — the camera
      // position and view direction stay put across orbit/fly toggles.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewUrl]);

    // ── Mode effect — swaps controls in/out without rebuilding the scene ──
    // Runs whenever `mode` changes. Camera position/quaternion are preserved
    // exactly because we don't touch them here; only the controls binding is
    // disposed and recreated. For Orbit, we derive a sensible target from the
    // splat's current bounding box so the pivot is the model (not origin).
    useEffect(() => {
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      // Don't gate on splat — the async fetch means splatRef.current is null
      // on initial mount, and bailing here left the viewer with no controls
      // binding until the user toggled modes. OrbitControls / SparkControls
      // only need camera + renderer; the splat is used for the orbit target
      // hint, and that has a working fallback below. Once the splat finishes
      // loading, frameToBoundingBox() updates the orbit target automatically.
      if (!camera || !renderer) return;
      const splat = splatRef.current;

      // Tear down any previous binding before installing the new one.
      controlsBindingRef.current?.dispose();

      if (mode === "orbit") {
        const orbit = new OrbitControls(camera, renderer.domElement);
        orbit.enableDamping = true;
        orbit.dampingFactor = 0.08;
        // Three.js OrbitControls default: left=rotate, right=pan, middle=dolly.
        orbit.screenSpacePanning = true;
        orbit.rotateSpeed = 0.9;
        orbit.panSpeed = 0.9;
        orbit.zoomSpeed = 1.0;

        // Pick a sensible target: splat center if the bounding box is ready,
        // otherwise a point 1 unit in front of where the camera is currently
        // looking. Either way the camera position is untouched.
        let target = new THREE.Vector3();
        try {
          if (splat) {
            splat.updateMatrixWorld(true);
            const box = splat.getBoundingBox();
            if (isFinite(box.min.x) && box.getSize(new THREE.Vector3()).length() > 0) {
              target = box.getCenter(new THREE.Vector3()).applyMatrix4(splat.matrixWorld);
            } else {
              const fwd = new THREE.Vector3();
              camera.getWorldDirection(fwd);
              target = camera.position.clone().add(fwd);
            }
          } else {
            const fwd = new THREE.Vector3();
            camera.getWorldDirection(fwd);
            target = camera.position.clone().add(fwd);
          }
        } catch {
          target = new THREE.Vector3();
        }
        orbit.target.copy(target);
        orbit.update();

        // WASD/QE keyboard panning. Translates camera AND orbit target by the
        // same vector so the pivot stays a fixed offset ahead — the next drag
        // still rotates around the visible scene rather than snapping back.
        // Speed scales with current camera→target distance so far-away views
        // step faster than close-ups (a fixed step is unusable across scales).
        const pressed = new Set<string>();
        function onKeyDown(e: KeyboardEvent) {
          const tag = (e.target as HTMLElement | null)?.tagName;
          if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
          const key = e.key.toLowerCase();
          if ("wasdqe".includes(key) || key === "shift" || key === "control") {
            pressed.add(key);
          }
        }
        function onKeyUp(e: KeyboardEvent) {
          pressed.delete(e.key.toLowerCase());
        }
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);

        let lastStepMs = performance.now();
        const FORWARD = new THREE.Vector3();
        const RIGHT = new THREE.Vector3();
        const UP = new THREE.Vector3(0, 1, 0);
        const step = () => {
          const now = performance.now();
          const dt = Math.min(0.05, (now - lastStepMs) / 1000);
          lastStepMs = now;

          if (pressed.size > 0) {
            const distance = camera.position.distanceTo(orbit.target);
            let speed = Math.max(0.4, distance) * 1.6; // units/sec
            if (pressed.has("shift")) speed *= 5;
            if (pressed.has("control")) speed *= 0.2;
            camera.getWorldDirection(FORWARD);
            RIGHT.crossVectors(FORWARD, UP).normalize();
            const delta = new THREE.Vector3();
            if (pressed.has("w")) delta.addScaledVector(FORWARD, speed * dt);
            if (pressed.has("s")) delta.addScaledVector(FORWARD, -speed * dt);
            if (pressed.has("d")) delta.addScaledVector(RIGHT, speed * dt);
            if (pressed.has("a")) delta.addScaledVector(RIGHT, -speed * dt);
            if (pressed.has("e")) delta.addScaledVector(UP, speed * dt);
            if (pressed.has("q")) delta.addScaledVector(UP, -speed * dt);
            if (delta.lengthSq() > 0) {
              camera.position.add(delta);
              orbit.target.add(delta);
            }
          }
          orbit.update();
        };

        controlsBindingRef.current = {
          kind: "orbit",
          orbit,
          step,
          dispose: () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            orbit.dispose();
          },
        };
      } else {
        // Reuse the cached spark instance if we have one; otherwise create.
        let spark = sparkControlsRef.current;
        if (!spark) {
          spark = new SparkControls({ canvas: renderer.domElement });
          sparkControlsRef.current = spark;
        }
        // Make sure the instance is active and starts with no drag carry-over.
        spark.pointerControls.enable = true;
        spark.fpsMovement.enable = true;
        spark.pointerControls.rotating = null;
        spark.pointerControls.sliding = null;
        const sparkInstance = spark;
        controlsBindingRef.current = {
          kind: "fly",
          spark: sparkInstance,
          step: () => sparkInstance.update(camera),
          dispose: () => {
            // Don't tear down — flipping enable off is enough to make the
            // listeners no-op. Clearing drag state prevents a half-rotation
            // from being applied the next time fly mode is selected.
            sparkInstance.pointerControls.enable = false;
            sparkInstance.fpsMovement.enable = false;
            sparkInstance.pointerControls.rotating = null;
            sparkInstance.pointerControls.sliding = null;
          },
        };
      }

      return () => {
        // Don't dispose here — the next-effect-run handles it, and the main
        // scene effect's cleanup handles unmount. Disposing here would race
        // with React's StrictMode double-invoke in dev.
      };
    }, [mode]);

    return (
      <div ref={containerRef} className={`${className ?? "h-full w-full"} relative`}>
        {/* Pick-in-progress spinner. Only mounts after a 120 ms delay (see
            handlePointerUp), so fast picks don't trigger a flash. Centered
            on the click point with pointer-events-none so it doesn't block
            subsequent interactions. */}
        {pickIndicator && (
          <div
            className="pointer-events-none absolute z-30 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-surface-900/80 backdrop-blur-sm ring-2 ring-brand-400/50"
            style={{ left: pickIndicator.x, top: pickIndicator.y }}
          >
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-400/30 border-t-brand-300" />
          </div>
        )}
      </div>
    );
  },
);
